import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { US_STATES } from "@/lib/listings";
import { STORE_TYPES, lookup } from "@/lib/grocery";
import {
  Search, MapPin, Phone, Store as StoreIcon, ShoppingBag, ShoppingCart,
  Plus, Sparkles, ListChecks
} from "lucide-react";

const StoreCard = ({ s, t, lang, onClick }) => (
  <button
    onClick={onClick}
    className="text-right group glass-card rounded-2xl overflow-hidden hover:border-yellow-500/60 transition-all purple-gold-glow"
    data-testid={`store-card-${s.store_id}`}
  >
    <div className="relative h-44 overflow-hidden bg-slate-900">
      <ListingImage path={s.logo || s.photos?.[0]} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
      <Badge className="absolute top-3 right-3 bg-gradient-to-r from-purple-500 to-yellow-500 text-white border-0">
        {lookup(STORE_TYPES, s.store_type, lang)}
      </Badge>
    </div>
    <div className="p-4 space-y-2">
      <h3 className="font-bold text-white truncate">{lang === "ar" ? (s.name_ar || s.name) : s.name}</h3>
      <p className="text-xs text-slate-400 line-clamp-2">{s.description}</p>
      <div className="flex items-center gap-1 text-xs text-slate-400 pt-1">
        <MapPin size={12} className="text-yellow-500" /> <span className="truncate">{s.city}, {s.state} · {s.zip_code}</span>
      </div>
    </div>
  </button>
);

export default function StoresMarketplace() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ query: "", store_type: "", state: "", city: "", zip_code: "" });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const res = await axios.get(`${API}/stores`, { params });
      setStores(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const inputCls = "bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("سوق البقالة العربية", "Arab Grocery Marketplace")}>
      <div className="space-y-6" data-testid="stores-marketplace">
        {/* Hero */}
        <div className="relative glass-card rounded-3xl p-6 lg:p-10 overflow-hidden bg-luxury">
          <div className="absolute -top-40 -right-40 w-[460px] h-[460px] bg-yellow-500/20 blur-[140px] rounded-full" />
          <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] bg-purple-500/20 blur-[140px] rounded-full" />
          <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30 mb-3">
                <ShoppingBag size={12} className="ml-1" /> {t("بقالة عربية", "Arab Grocery")}
              </Badge>
              <h2 className="text-3xl lg:text-5xl font-bold mb-2 leading-tight text-luxury">
                {t("كل ما تحتاجه من المنزل العربي", "Everything from the Arab pantry")}
              </h2>
              <p className="text-slate-400 max-w-2xl">
                {t("متاجر حلال، ملاحم، مخابز، حلويات — توصيل سريع لباب بيتك.", "Halal markets, butchers, bakeries, sweet shops — fast delivery to your door.")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={() => navigate("/grocery/list")} variant="outline" className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10" data-testid="create-shopping-list-btn">
                <ListChecks className="ml-2" size={18} /> {t("قائمة تسوق", "Shopping list")}
              </Button>
              <Button onClick={() => navigate("/grocery/orders")} variant="outline" className="border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10" data-testid="my-orders-btn">
                <ShoppingCart className="ml-2" size={18} /> {t("طلباتي", "My orders")}
              </Button>
              <Button onClick={() => navigate("/grocery/mystore")} className="btn-primary" data-testid="my-store-btn">
                <StoreIcon className="ml-2" size={18} /> {t("متجري", "My store")}
              </Button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="glass-card rounded-2xl p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
          <input className={`${inputCls} md:col-span-2`} placeholder={t("ابحث عن متجر...", "Search stores...")}
            value={filters.query} onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="store-filter-query" />
          <select className={inputCls} value={filters.store_type} onChange={(e) => setFilters({ ...filters, store_type: e.target.value })} data-testid="store-filter-type">
            <option value="">{t("كل الأنواع", "All types")}</option>
            {STORE_TYPES.map((s) => <option key={s.v} value={s.v}>{language === "ar" ? s.ar : s.en}</option>)}
          </select>
          <select className={inputCls} value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} data-testid="store-filter-state">
            <option value="">{t("كل الولايات", "All states")}</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
          </select>
          <input className={inputCls} placeholder={t("ZIP", "ZIP")} value={filters.zip_code}
            onChange={(e) => setFilters({ ...filters, zip_code: e.target.value })} data-testid="store-filter-zip" />
          <Button onClick={() => load()} className="btn-primary" data-testid="store-search-btn">
            <Search size={16} className="ml-2" /> {t("بحث", "Search")}
          </Button>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map((i) => <div key={i} className="glass-card rounded-2xl h-72 skeleton" />)}
          </div>
        ) : stores.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <StoreIcon className="text-slate-600 mx-auto mb-3" size={40} />
            <p className="text-slate-400 mb-2">{t("لا توجد متاجر معتمدة بعد", "No approved stores yet")}</p>
            <p className="text-slate-500 text-sm mb-4">{t("يمكنك إنشاء قائمة تسوق وسيتولّى فريقنا الشراء لك", "Create a shopping list — our team will shop for you")}</p>
            <Button onClick={() => navigate("/grocery/list")} className="btn-primary">{t("أنشئ قائمة تسوق", "Create shopping list")}</Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {stores.map((s) => <StoreCard key={s.store_id} s={s} t={t} lang={language} onClick={() => navigate(`/grocery/store/${s.store_id}`)} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
